/**
 * Toast Container Component
 * 
 * Renders toast notifications in a fixed position at the bottom right of the screen.
 * Automatically animates toasts in and out.
 */

import { useToastStore, type ToastType } from '../stores/toast';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  X,
} from 'lucide-react';
import { cn } from '../lib/utils';

// ============================================================================
// Toast Styles
// ============================================================================

const toastStyles: Record<ToastType, { bg: string; border: string; icon: string; iconBg: string }> = {
  success: {
    bg: 'bg-white',
    border: 'border-green-200',
    icon: 'text-green-600',
    iconBg: 'bg-green-100',
  },
  error: {
    bg: 'bg-white',
    border: 'border-red-200',
    icon: 'text-red-600',
    iconBg: 'bg-red-100',
  },
  warning: {
    bg: 'bg-white',
    border: 'border-amber-200',
    icon: 'text-amber-600',
    iconBg: 'bg-amber-100',
  },
  info: {
    bg: 'bg-white',
    border: 'border-blue-200',
    icon: 'text-blue-600',
    iconBg: 'bg-blue-100',
  },
};

const toastIcons: Record<ToastType, React.ElementType> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

// ============================================================================
// Toast Item Component
// ============================================================================

interface ToastItemProps {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  onDismiss: (id: string) => void;
}

function ToastItem({ id, type, title, message, action, onDismiss }: ToastItemProps) {
  const styles = toastStyles[type];
  const Icon = toastIcons[type];

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-4 rounded-lg shadow-lg border min-w-[320px] max-w-[420px]',
        'animate-in slide-in-from-right-full fade-in duration-300',
        styles.bg,
        styles.border
      )}
      role="alert"
    >
      {/* Icon */}
      <div className={cn('flex-shrink-0 p-1.5 rounded-full', styles.iconBg)}>
        <Icon className={cn('w-4 h-4', styles.icon)} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {title && (
          <p className="font-medium text-slate-900 text-sm">{title}</p>
        )}
        <p className={cn('text-sm text-slate-600', title && 'mt-0.5')}>
          {message}
        </p>
        
        {/* Action Button */}
        {action && (
          <button
            onClick={() => {
              action.onClick();
              onDismiss(id);
            }}
            className={cn(
              'mt-2 text-sm font-medium hover:underline',
              type === 'error' ? 'text-red-600' :
              type === 'success' ? 'text-green-600' :
              type === 'warning' ? 'text-amber-600' :
              'text-blue-600'
            )}
          >
            {action.label}
          </button>
        )}
      </div>

      {/* Dismiss Button */}
      <button
        onClick={() => onDismiss(id)}
        className="flex-shrink-0 p-1 hover:bg-slate-100 rounded transition-colors"
        aria-label="Dismiss notification"
      >
        <X className="w-4 h-4 text-slate-400" />
      </button>
    </div>
  );
}

// ============================================================================
// Toast Container Component
// ============================================================================

export function ToastContainer() {
  const toasts = useToastStore((state) => state.toasts);
  const removeToast = useToastStore((state) => state.removeToast);

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          {...toast}
          onDismiss={removeToast}
        />
      ))}
    </div>
  );
}
